import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { TrendingUp, Shield, Globe, Clock, Package, DollarSign } from "lucide-react";
import RolePreviewLayout from "@/components/welcome/RolePreviewLayout";
import { MultiLayerValue } from "@/components/welcome/MultiLayerValue";
import { analytics, getVariant } from "@/lib/analytics";

const accent = {
  text: "text-purple-400", border: "border-purple-400/30",
  bgSoft: "bg-purple-400/5", bg: "bg-purple-400",
};

export default function WelcomeExporter() {
  const [params] = useSearchParams();
  const name = params.get("name") || "there";
  const variant = getVariant("welcome_exporter");
  useEffect(() => { analytics.rolePreviewViewed("exporter", variant); }, [variant]);
  const isB = variant === "b";

  return (
    <>
    <RolePreviewLayout
      role="exporter"
      accent={accent}
      eyebrow={`Application received · Exporter · variant ${variant.toUpperCase()}`}
      headline={isB
        ? <>Welcome, <span className="text-purple-400">{name}</span>. <br className="hidden sm:block" />Scale orders. <span className="text-[#22C55E]">Get paid 2× faster.</span></>
        : <>Welcome, <span className="text-purple-400">{name}</span>. <br className="hidden sm:block" />Faster receivables. Stronger cash flow.</>}
      subline={isB
        ? "Pre-vetted importers come to you with capital pre-secured. No more chasing buyers, no more 75-day collections — just shipping, delivery, and capital released. Free yourself to scale."
        : "As a verified exporter, you'll see your capital released against milestones in days — not 60-75. More turns per year, more growth, less working-capital pressure."}
      primaryStat={isB ? { value: "$0", label: "Receivables aging" } : { value: "2x–4x", label: "Capital turns per year" }}
      benefits={[
        { icon: Clock, title: "30–40 day cycles", desc: "Replace 60-75 day receivables with milestone-linked release. 20-35 days faster cash conversion." },
        { icon: Globe, title: "Pre-vetted importer pool", desc: "Buyers come pre-KYC'd. No more chasing collections — capital is escrowed before shipping." },
        { icon: TrendingUp, title: "25–40% working capital lift", desc: "Free up capital to scale production, take more orders, hire more people." },
      ]}
      dashboard={
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Active Flows", "2", "text-white"],
              ["Capital In Transit", "$475K", "text-purple-400"],
              ["Days to Receive", "32d", "text-[#22C55E]"],
              ["Verified Buyers", "8", "text-blue-400"],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-[#050A07] border border-[#1A2E22] p-3">
                <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">{l}</p>
                <p className={`font-['Outfit'] text-xl font-bold ${c}`}>{v}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#050A07] border border-[#1A2E22] p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white text-sm font-semibold">50 MT Black Tiger Shrimp HOSO 16/20 · CIF Houston</span>
              <span className="text-purple-400 text-[10px] border border-purple-400/30 px-2 py-0.5">Capital Secured · Shipped</span>
            </div>
            <div className="grid grid-cols-5 gap-1 mt-3">
              {["Capital Secured", "Execution", "Shipped", "Delivered", "Released"].map((m, i) => (
                <div key={m} className={`text-[9px] py-1 px-1.5 text-center border ${i <= 2 ? "border-[#22C55E]/40 text-[#22C55E] bg-[#22C55E]/5" : "border-[#1A2E22] text-[#A1B2A8]/40"}`}>{m}</div>
              ))}
            </div>
            <p className="text-[#A1B2A8] text-[10px] mt-2">Capital release in <span className="text-[#22C55E]">~14 days</span> (vs traditional 60+).</p>
          </div>
        </div>
      }
      nextSteps={[
        { title: "Document collection (2-3 days)", desc: "We aggregate authoritative public-registry signals automatically and request bank refs + export licenses." },
        { title: "Trust score generated", desc: "Your AI Confidence score determines preferred capital structures (T/T, L/C, Escrow)." },
        { title: "First buyer match", desc: "Pre-vetted importers tap your verified inventory — capital secured before the container moves." },
      ]}
    />
    <MultiLayerValue persona="exporter" />
    </>
  );
}
